<template>
  <div>
    <e-filters
      :title="$t('Filtros')"
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="6">
          <e-store-combo
            id="delivery-config-filter-store"
            v-model="filters.storeId"
            :required="false"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      ref="table-card"
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-row>
        <b-col md="12">
          <b-table
            id="delivery-config-table"
            class="table-responsive bordered"
            responsive
            striped
            show-empty
            :empty-text="$t('Nenhuma configuração encontrada.')"
            :items="rows"
            :fields="fields"
          >
            <template #cell(actions)="row">
              <e-grid-actions
                :show-delete="false"
                :show-update="$can('Update', 'Store')"
                :buttons="campaignButton"
                @update="edit(row.item)"
                @campaigns="goToCampaigns(row.item)"
              />
            </template>

            <template #cell(scope)="{ item }">
              <b-badge :variant="item.scope === 'general' ? 'light-primary' : 'light-secondary'">
                {{ item.scope === 'general' ? $t('Geral') : $t('Loja') }}
              </b-badge>
            </template>

            <template #cell(name)="{ item }">
              <feather-icon
                :icon="item.scope === 'general' ? 'GlobeIcon' : 'HomeIcon'"
                class="mr-50"
              />
              <strong>{{ item.scope === 'general' ? $t('Geral (todas as lojas)') : item.storeName }}</strong>
            </template>

            <template #cell(situation)="{ item }">
              <b-badge :variant="situationVariant(item)">
                {{ situationLabel(item) }}
              </b-badge>
            </template>

            <template #cell(campaignCount)="{ item }">
              <b-badge
                v-if="item.activeCampaignCount > 0"
                variant="light-success"
              >
                {{ item.activeCampaignCount }} {{ item.activeCampaignCount === 1 ? $t('ativa') : $t('ativas') }}
              </b-badge>
              <b-badge
                v-else-if="item.campaignCount > 0"
                variant="light-secondary"
              >
                {{ item.campaignCount }} {{ item.campaignCount === 1 ? $t('campanha') : $t('campanhas') }}
              </b-badge>
              <span v-else>—</span>
            </template>
          </b-table>

          <b-row>
            <b-col md="6">
              <e-pagination-summary
                :current-page="paging.currentPage"
                :per-page="paging.pageSize"
                :total="paging.rowCount"
                :total-on-page="paging.rowsInCurrentPage"
              />
            </b-col>
            <b-col md="6">
              <b-pagination
                v-model="paging.currentPage"
                align="right"
                :total-rows="paging.rowCount"
                :per-page="paging.pageSize"
                aria-controls="delivery-config-table"
                @change="pageChange"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card-actions>

    <FAB
      v-if="$can('Update', 'Store')"
      :main-tooltip="$t('Configurar uma loja')"
      @click="newStoreConfig"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { BTable, BRow, BCol, BBadge, BPagination } from 'bootstrap-vue'
import { FAB, EFilters, EPageSizeSelector, EPaginationSummary } from '@/views/components'
import { alerts } from '@/mixins'
import EGridActions from '@/views/components/EGridActions.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'

/**
 * Tela de listagem da Configuração de Delivery (D28/D29): ponto de entrada em
 * `/lojas/configuracao-delivery`. Mostra a Geral (fixa no topo) e as lojas que têm configuração
 * PRÓPRIA — paginadas no padrão do sistema — com filtro por loja. Cada linha leva à tela de
 * manutenção (`delivery-config-manage`) já no escopo escolhido.
 */
export default {
  components: {
    BCardActions,
    BTable,
    BRow,
    BCol,
    BBadge,
    BPagination,
    FAB,
    EFilters,
    EPageSizeSelector,
    EPaginationSummary,
    EGridActions,
    EStoreCombo,
  },

  mixins: [alerts],

  data() {
    return {
      fetching: false,
      filters: {
        storeId: null,
      },
    }
  },

  computed: {
    ...mapState('pages/store/deliveryConfig', ['scopeSummary', 'generalSummary', 'paging']),

    // Geral sempre no topo (fixa, fora da paginação); as lojas próprias vêm paginadas do servidor.
    rows() {
      const general = this.generalSummary ? [this.generalSummary] : []
      return [...general, ...this.scopeSummary]
    },

    // Botão extra "Campanhas" nas ações da linha (vai para a tela de campanhas do escopo).
    campaignButton() {
      return [
        {
          event: 'campaigns',
          icon: 'megaphone',
          variant: 'outline-primary',
          title: this.$t('Campanhas'),
        },
      ]
    },

    fields() {
      return [
        {
          key: 'actions',
          label: this.$t('Ações'),
          class: 'text-center',
          thStyle: { width: '110px' },
        },
        {
          key: 'scope',
          label: this.$t('Escopo'),
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          key: 'name',
          label: this.$t('Configuração'),
        },
        {
          key: 'situation',
          label: this.$t('Situação'),
          class: 'text-center',
          thStyle: { width: '160px' },
        },
        {
          key: 'campaignCount',
          label: this.$t('Campanhas'),
          class: 'text-center',
          thStyle: { width: '110px' },
        },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('pages/store/deliveryConfig', [
      'fetchScopeSummary',
      'setCurrentPage',
      'setPageSize',
    ]),

    async getData() {
      try {
        this.fetching = true
        await this.fetchScopeSummary({ storeId: this.filters.storeId })
      } catch (error) {
        this.showGenericError({ error })
      } finally {
        this.fetching = false
      }
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.filters.storeId = null
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    situationLabel(item) {
      if (item.scope === 'general') {
        return item.hasBase ? this.$t('Configurada') : this.$t('Não configurada')
      }
      return this.$t('Configuração própria')
    },

    situationVariant(item) {
      if (item.scope === 'general') {
        return item.hasBase ? 'light-success' : 'light-warning'
      }
      return 'light-success'
    },

    edit(item) {
      this.$router.push({ name: 'delivery-config-manage', query: this.scopeQuery(item) })
    },

    goToCampaigns(item) {
      this.$router.push({ name: 'delivery-config-campaigns', query: this.scopeQuery(item) })
    },

    scopeQuery(item) {
      const query = { scope: item.scope }
      if (item.storeId) query.storeId = item.storeId
      return query
    },

    // Abre a manutenção no escopo de loja (sem loja escolhida) para criar uma configuração própria.
    newStoreConfig() {
      this.$router.push({ name: 'delivery-config-manage', query: { scope: 'store' } })
    },
  },
}
</script>

<style></style>
